<template>
  <div>
    <!-- Notificazione -->
    <Notification
      :show="notification.show"
      :type="notification.type"
      :title="notification.title"
      :message="notification.message"
      @close="notification.show = false"
    />
    
    <div class="p-4">
      <BreadCrumbs :navigation="navigation" />
      <div class="flex items-center justify-between pt-4 mb-6">
        <div class="flex items-center gap-4">
          <h1 class="text-xl font-bold text-gray-900">Sblocco ingressi</h1>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-gray-600 text-sm hidden sm:block">Aggiornamento tra: {{ remainingSeconds }}s</span>
          <button 
            @click="refreshData" 
            class="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-md flex items-center"
            title="Aggiorna ora"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Riassunto veloce per mobile -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
          <div class="text-lg font-bold text-yellow-700">{{ pendingVisits.length }}</div>
          <div class="text-xs text-yellow-600">Visite in Attesa</div>
        </div>
        <div class="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
          <div class="text-lg font-bold text-purple-700">{{ pendingSignatures.length }}</div>
          <div class="text-xs text-purple-600">Da Firmare</div>
        </div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <div class="text-lg font-bold text-red-700">{{ pendingAssistance.length }}</div>
          <div class="text-xs text-red-600">Assistenza</div>
        </div>
      </div>

      <!-- Sezione Visite in Attesa -->
      <div v-if="pendingVisits.length > 0" class="mb-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <svg class="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          Visite in Attesa di Conferma ({{ pendingVisits.length }})
        </h2>
        <div class="space-y-3">
          <div v-for="visit in pendingVisits" :key="visit.id" 
               class="bg-white border border-yellow-200 rounded-lg p-4 shadow-sm">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div class="flex-1 mb-3 sm:mb-0">
                <div class="flex items-start">
                  <div class="flex-1">
                    <h3 class="font-medium text-gray-900">{{ visit.visitor_name }}</h3>
                    <p class="text-sm text-gray-600">{{ visit.visitor_company }}</p>
                    <p class="text-xs text-gray-500 mt-1">
                      Badge: {{ visit.badge?.code || 'N/A' }} • 
                      {{ formatDateTime(visit.check_in_at) }}
                    </p>
                    <p v-if="visit.contact" class="text-xs text-gray-500">
                      Referente: {{ visit.contact.name }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex gap-2">
                <button 
                  @click="confirmVisit(visit.id)"
                  class="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Conferma
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sezione Consegne con Firma -->
      <div v-if="pendingSignatures.length > 0" class="mb-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <svg class="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
          </svg>
          Consegne da Firmare ({{ pendingSignatures.length }})
        </h2>
        <div class="space-y-3">
          <div v-for="delivery in pendingSignatures" :key="delivery.id" 
               class="bg-white border border-purple-200 rounded-lg p-4 shadow-sm">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div class="flex-1 mb-3 sm:mb-0">
                <h3 class="font-medium text-gray-900">Consegna per {{ delivery.recipient }}</h3>
                <p class="text-sm text-gray-600">{{ getCompanyName(delivery.company_destination) }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  {{ formatDateTime(delivery.created_at) }}
                </p>
              </div>
              <div class="flex gap-2">
                <button 
                  @click="confirmDelivery(delivery.id)"
                  class="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
                  </svg>
                  Firmato
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sezione Richieste di Assistenza -->
      <div v-if="pendingAssistance.length > 0" class="mb-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <svg class="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
          </svg>
          Richieste di Assistenza ({{ pendingAssistance.length }})
        </h2>
        <div class="space-y-3">
          <div v-for="request in pendingAssistance" :key="request.id" 
               class="bg-white border border-red-200 rounded-lg p-4 shadow-sm">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div class="flex-1 mb-3 sm:mb-0">
                <h3 class="font-medium text-gray-900">Richiesta di Assistenza</h3>
                <p class="text-xs text-gray-500 mt-1">
                  {{ formatDateTime(request.created_at) }}
                </p>
              </div>
              <div class="flex gap-2">
                <button 
                  @click="confirmAssistance(request.id)"
                  class="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Gestita
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Messaggio quando non ci sono elementi pendenti -->
      <div v-if="!hasPendingItems" class="text-center py-4">
        <h3 class="text-lg font-medium text-gray-900 mb-2">Tutto in ordine!</h3>
        <p class="text-gray-500">Non ci sono ingressi che richiedono la tua attenzione al momento.</p>
        <Link 
          :href="route('modules.rd-dashboard')"
          class="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          Vai al reporto odierno
        </Link>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Notification from '@/Components/Notification.vue';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

export default {
  props: [
    'pendingVisits', 
    'pendingDeliveries', 
    'pendingAssistance',
    'companies'
  ],
  components: {
    Link,
    BreadCrumbs,
    Notification
  },
  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.reception-digitale'), href: route('modules.home', 'reception-digitale') },
        { name: 'Sblocco ingressi', href: route('modules.rd-manage-entries') }
      ],
      refreshInterval: 15, // Refresh più frequente per la gestione
      remainingSeconds: 15,
      timerId: null,
      notification: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      }
    };
  },
  computed: {
    pendingSignatures() {
      return this.pendingDeliveries ? this.pendingDeliveries.filter(d => d.delivery_type === 'con-firma') : [];
    },
    hasPendingItems() {
      return (this.pendingVisits && this.pendingVisits.length > 0) ||
             (this.pendingDeliveries && this.pendingDeliveries.length > 0) ||
             (this.pendingAssistance && this.pendingAssistance.length > 0);
    }
  },
  mounted() {
    this.startRefreshTimer();
  },
  beforeUnmount() {
    this.clearRefreshTimer();
  },
  methods: {
    formatDateTime(dateString) {
      if (!dateString) return '';
      try {
        return format(parseISO(dateString), 'dd/MM HH:mm', { locale: it });
      } catch (error) {
        return dateString;
      }
    },
    getCompanyName(companyKey) {
      return this.companies[companyKey] || companyKey;
    },
    startRefreshTimer() {
      this.clearRefreshTimer();
      this.remainingSeconds = this.refreshInterval;
      
      this.timerId = setInterval(() => {
        this.remainingSeconds--;
        if (this.remainingSeconds <= 0) {
          this.refreshData();
        }
      }, 1000);
    },
    clearRefreshTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },
    refreshData() {
      router.reload({ 
        preserveScroll: true, 
        onSuccess: () => {
          this.startRefreshTimer();
        }
      });
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        type,
        title,
        message
      };
    },
    confirmVisit(visitId) {
      router.post(route('modules.rd-conferma-visita', visitId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Visita Confermata', 'La visita è stata confermata con successo.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante la conferma della visita.');
        }
      });
    },
    confirmDelivery(deliveryId) {
      router.post(route('modules.rd-conferma-consegna', deliveryId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Consegna Confermata', 'La consegna è stata confermata con successo.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante la conferma della consegna.');
        }
      });
    },
    depositDelivery(deliveryId) {
      router.post(route('modules.rd-deposit-delivery', deliveryId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Consegna Depositata', 'La consegna è stata marcata come depositata.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante il deposito della consegna.');
        }
      });
    },
    confirmAssistance(requestId) {
      router.post(route('modules.rd-conferma-assistenza', requestId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Assistenza Gestita', 'La richiesta di assistenza è stata gestita.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante la gestione della richiesta di assistenza.');
        }
      });
    }
  }
};
</script>
